import React, { useEffect } from "react";
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { IconButton, Stack } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from 'dashboard/components/common';
import TableManagement from "dashboard/components/tableList";
import { API } from "utilities/api";
import { useOutletContext } from "react-router";
import AnalysisHeader from "dashboard/components/analysisHeader";
import StageDetails from "dashboard/pages/webpage/stageDetails";
import DetailPageCard from "dashboard/pages/webpage/DetailPageCard";
import { libraryLabel, libraryHint } from "utilities/libraryLabels";
import { toast } from "react-toastify";


/**
 * Column headers from the section's own field definitions.
 *
 * Previously these came from `Object.keys(entries[0])`, which meant an empty
 * section had no columns at all, and a filled one showed raw field names in
 * whatever order Mongo returned them. The schema knows the fields whether or
 * not any entry exists yet.
 */
const HIDDEN_FIELDS = ['_id', '__v', 'brand_id', 'brand_id', 'draft', 'published', 'creator', 'user_updated', 'created_at', 'updated_at'];

const titleCase = (k) => String(k)
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, (c) => c.toUpperCase());

function columnsFromSchema(stageModal) {
  // `stage` from the API is a flat array of field NAMES —
  // ['title', 'content', 'image'] — not objects. Reading `.fields`/`.inputs`
  // off it silently produced an empty list, so every section with no entries
  // yet rendered a table containing nothing but an ACTIONS column. Most
  // sections start empty, so that was all of them.
  const fields = (Array.isArray(stageModal) ? stageModal : [])
    .filter((n) => typeof n === 'string' && !HIDDEN_FIELDS.includes(n));
  const cols = fields.map((n) => ({ Header: titleCase(n), accessor: n }));
  cols.push({ Header: 'ACTIONS', accessor: 'actions' });
  return cols;
}

export default function WebPage(props) {
  const [selectedBrand] = useOutletContext();
  const { stage } = props
  const navigate = useNavigate();

  const [tableData, setTableData] = React.useState([]);
  const [columnsData, setColumnsData] = React.useState([]);
  const [stageModal, setStageModal] = React.useState();
  const [stageLayout, setStageLayout] = React.useState();
  // Whether the first load has finished — not whether there is data. The
  // table renders either way; an empty one is how you add the first entry.
  const [ready, setReady] = React.useState(false);
  // {
  //     title: "",
  //     value: "",
  //     icon: "",
  // }
  const [headerData, setHeaderData] = React.useState([]);
  function getPageData() {
    return API.getStageByBrandId({ brand_id: selectedBrand, stage }).then((res) => {
      if (!res.entries || res.entries.length === 0) {
        // An empty section used to render a table of fake ORDERS — product
        // name, email, total price — because the placeholder dataset from an
        // unrelated screen was loaded whenever there was nothing real. A
        // brand opening "Our Team" for the first time was shown invented
        // customer orders and no way to tell they were not their own data.
        //
        // Empty is now empty: the table renders with the section's own
        // columns and nothing in it, which is the truth and leaves the Add
        // button as the obvious next step.
        setTableData([])
        setColumnsData(columnsFromSchema(res.stage))
        setStageModal(res.stage)
        setStageLayout(res.layout)
        setHeaderData([])
        setReady(true)
      } else {
        setTableData(res.entries)
        setStageModal(res.stage)
        setStageLayout(res.layout)
        const keys = Object.keys(res.entries[0]).filter(key => !HIDDEN_FIELDS.includes(key));
        const headersArray = keys.map(key => ({ Header: titleCase(key), accessor: key}));
        headersArray.push({ Header: 'ACTIONS', accessor: 'actions' })
        setColumnsData(headersArray)
        setHeaderData(res.analysis)
        setReady(true)
      }
    }).catch((err) => {
      // The table carries the Add button, so a failed load used to leave the
      // screen with no way to add anything and nothing saying why — which
      // looks exactly like a section that does not support adding. Show the
      // table with its columns, and say what went wrong.
      console.error('getPageData', err);
      toast.error(err?.message || `Could not load your ${stage}`);
      setTableData([]);
      setReady(true);
    })
  }

  useEffect(() => {
    getPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])


  const handleCreateUpdateStageData = (formData, imageFile) => {
    const newformData = new FormData();
    newformData.append('data', JSON.stringify({ ...formData, stage, brand_id: selectedBrand }));
    if (imageFile && imageFile !== null) {
      newformData.append('file', imageFile);
    }
    return API.insertUpdateItemToStage(newformData).then((response) => {
      toast.success('Your data has been submitted!');
      getPageData();
    }).catch((error) => {
      toast.error(error);
    });
  };

  const handleDelete = (formData) => {
    return API.deleteItemFromStage({ ...formData, stage, brand_id: selectedBrand }).then((response) => {
      toast.success('Your data has been deleted!');
      getPageData();
    }).catch((error) => {
      toast.error(error);
    });
  }


  return (
    <Stack direction='row' sx={{ pt: CONTENT_TOP }}>
      <Stack direction='column' sx={{ width: '100%' }}>
        {/* This screen is one of twenty-six inside the library, and had no way
            back to it — you either used the browser's button or the sidebar,
            neither of which is where you look. */}
        <PageHeader
          title={libraryLabel(stage)}
          subtitle={libraryHint(stage)}
          leading={(
            <IconButton
              onClick={() => navigate('/dashboard/webpage/library')}
              aria-label='Back to the content library'
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
        />
        <AnalysisHeader data={headerData} />
        {/* Writing an entry and deciding what its page looks like are the same
            job to the person doing it, and they were in different parts of the
            dashboard with nothing linking them. */}
        <DetailPageCard stage={stage} brandId={selectedBrand} />
        <StageDetails stageLayout={stageLayout} stage={stage} selectedBrand={selectedBrand} />
        {ready && (
          <TableManagement
            getPageData={getPageData}
            tableData={tableData}
            columnsData={columnsData}
            name={stage}
            popupModal={stageModal}
            selectedBrand={selectedBrand}
            handleCreate={handleCreateUpdateStageData}
            handleDelete={handleDelete}
            handleEdit={handleCreateUpdateStageData}
          />
        )}
      </Stack>
    </Stack>
  );
}
